import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Home, Wallet, ShieldCheck } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Card } from "@/components/ui/card";
import { PropertyDetails as PropertyDetailsComponent } from "@/components/real-estate/PropertyDetails";
import { PropertyMarketAnalysis } from "@/components/real-estate/PropertyMarketAnalysis";
import { PropertyNeighborhood } from "@/components/real-estate/PropertyNeighborhood";
import { PropertyDocumentation } from "@/components/real-estate/PropertyDocumentation";
import { SimilarProperties } from "@/components/real-estate/SimilarProperties";
import { PropertyReviews } from "@/components/real-estate/PropertyReviews";
import { PropertyDiscussions } from "@/components/real-estate/PropertyDiscussions";
import { PropertyExpertAnalysis } from "@/components/real-estate/PropertyExpertAnalysis";
import { RealEstateNavBar } from "@/components/real-estate/RealEstateNavBar";
import { Alert } from "@/components/ui/custom-alert";
import type { Tables } from "@/integrations/supabase/types";
import type { MarketAnalysis, NeighborhoodStats, PropertyDocumentation as PropertyDocumentationType } from "@/integrations/supabase/types/tables/realEstate";
import { useState } from "react";

const PropertyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [collateralLoading, setCollateralLoading] = useState(false);
  const [isPurchased, setIsPurchased] = useState(false);

  const { data: property, isLoading } = useQuery({
    queryKey: ["property", id],
    queryFn: async () => {
      const { data: propertyData, error: propertyError } = await supabase
        .from("real_estate_properties")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (propertyError || !propertyData) {
        toast({
          title: "Error",
          description: "Could not load property details",
          variant: "destructive",
        });
        throw propertyError || new Error("Property not found");
      }

      if (propertyData.similar_properties && Array.isArray(propertyData.similar_properties)) {
        const { data: similarProps, error: similarError } = await supabase
          .from("real_estate_properties")
          .select("*")
          .in("id", propertyData.similar_properties as string[]);

        if (similarError) {
          console.error("Error fetching similar properties:", similarError);
        }

        return {
          ...propertyData,
          similar_properties: similarProps || []
        } as Tables<"real_estate_properties"> & { similar_properties: Tables<"real_estate_properties">[] };
      }

      return {
        ...propertyData,
        similar_properties: []
      } as Tables<"real_estate_properties"> & { similar_properties: Tables<"real_estate_properties">[] };
    },
  });

  const { data: ownershipData, isLoading: ownershipLoading } = useQuery({
    queryKey: ["property-ownership", id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { owned: false };
      
      const { data, error } = await supabase
        .from("collateral_properties")
        .select("*")
        .eq("property_id", id)
        .eq("borrower_id", user.id)
        .maybeSingle();
      
      if (error) {
        console.error("Error checking property ownership:", error);
        return { owned: false };
      }
      
      if (data) {
        setIsPurchased(true);
        return { owned: true, collateral: data };
      }
      
      return { owned: false };
    },
  });
  
  const handlePurchaseProperty = async () => {
    setPurchaseLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Please sign in",
          description: "You need to be signed in to purchase property",
          variant: "destructive",
        });
        navigate('/signin?redirect=' + encodeURIComponent(`/real-estate/${id}`));
        return;
      }
      
      const { data, error } = await supabase
        .from('collateral_properties')
        .insert({
          borrower_id: user.id,
          property_id: id,
          collateral_value: property.price,
          status: 'available',
          investment_type: 'property',
          investment_details: {
            property_type: property.property_type,
            location: property.location,
            purchase_price: property.price
          }
        })
        .select()
        .single();
      
      if (error) throw error;
      
      toast({
        title: "Property Purchased!",
        description: "The property has been added to your portfolio and is available as collateral.",
      });
      
      setIsPurchased(true);
    } catch (error) {
      console.error("Error purchasing property:", error);
      toast({
        title: "Purchase Failed",
        description: "There was an error processing your purchase. Please try again.",
        variant: "destructive",
      });
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handleUseAsCollateral = () => {
    setCollateralLoading(true);
    navigate(`/pre-approval?property=${id}`);
    setCollateralLoading(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="animate-pulse">
          <div className="h-64 bg-gray-200 rounded-lg mb-6"></div>
          <div className="h-8 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-2xl font-bold mb-4">Property not found</h1>
          <Button onClick={() => navigate("/real-estate")}>Back to Properties</Button>
        </div>
      </div>
    );
  }

  const marketAnalysis = property.market_analysis as unknown as MarketAnalysis;
  const neighborhoodStats = property.neighborhood_stats as unknown as NeighborhoodStats;
  const documentation = property.documentation as unknown as PropertyDocumentationType;

  return (
    <div className="min-h-screen bg-white">
      <RealEstateNavBar />
      
      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate("/real-estate")}
          className="mb-6 p-2 hover:bg-gray-100 rounded-full inline-flex items-center"
          aria-label="Go back"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="aspect-video bg-gradient-to-br from-purple-100/50 to-purple-50 rounded-lg relative">
              {property.images?.[0] && (
                <img
                  src={property.images[0]}
                  alt={property.title}
                  className="w-full h-full object-cover rounded-lg"
                />
              )}
            </div>

            <PropertyDetailsComponent property={property} />
            <PropertyMarketAnalysis marketAnalysis={marketAnalysis} />
            <PropertyNeighborhood neighborhoodStats={neighborhoodStats} />
            <PropertyDocumentation documentation={documentation} />
            <PropertyReviews propertyId={property.id} />
            <PropertyDiscussions propertyId={property.id} />
            <PropertyExpertAnalysis propertyId={property.id} />
          </div>

          <div className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Investment Details</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Risk Rating</span>
                  <div className="flex items-center">
                    <span className="font-medium mr-2">{property.risk_rating}/10</span>
                  </div>
                </div>
                
                <div className="pt-4">
                  <div className="flex flex-col gap-3">
                    {!isPurchased && !ownershipData?.owned ? (
                      <Button 
                        onClick={handlePurchaseProperty} 
                        disabled={purchaseLoading || property.status !== 'available'}
                        className="w-full flex items-center gap-2"
                      >
                        <Home className="h-4 w-4" />
                        {purchaseLoading ? "Processing..." : "Purchase Property"}
                      </Button>
                    ) : (
                      <>
                        <Alert variant="success" className="p-3 rounded-md">
                          <div className="flex items-center text-green-800 gap-2">
                            <Wallet className="h-4 w-4" />
                            <span className="font-medium">You own this property</span>
                          </div>
                          <p className="text-sm text-green-700 mt-1">
                            Value: {new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(property.price)}
                          </p>
                        </Alert>
                        
                        <Button 
                          onClick={handleUseAsCollateral}
                          variant="outline"
                          className="w-full flex items-center gap-2 border-green-500 text-green-800 hover:bg-green-50"
                        >
                          <ShieldCheck className="h-4 w-4" />
                          Use as Loan Collateral
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            {property.similar_properties.length > 0 && (
              <SimilarProperties properties={property.similar_properties} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetails;
